import { useCallback, useEffect } from 'react';
import useLocalStorage from './useLocalStorage';

const useColumnVisibility = (columns, componentName, toggleHideColumn) => {
  const [columnVisibility, setColumnVisibility] = useLocalStorage(
    `${componentName}ColumnVisibility`,
    columns.reduce((acc, col) => {
      const key = col.accessor ?? col.id;
      if (!key) return acc;
      acc[key] = !col.defaultHidden;
      return acc;
    }, {}),
  );

  useEffect(() => {
    const missingDefaultHidden = columns.filter((col) => {
      const key = col.accessor ?? col.id;
      return col.defaultHidden && key && !(key in columnVisibility);
    });
    if (missingDefaultHidden.length === 0) return;
    setColumnVisibility((prev) => {
      const next = { ...prev };
      missingDefaultHidden.forEach((col) => {
        const key = col.accessor ?? col.id;
        next[key] = false;
      });
      return next;
    });
  }, [columns, columnVisibility, setColumnVisibility]);

  const toggleColumnVisibility = useCallback(
    (accessor) => {
      setColumnVisibility((prev) => ({
        ...prev,
        [accessor]: !prev[accessor],
      }));
    },
    [setColumnVisibility],
  );

  const getHiddenColumns = useCallback(() => {
    const alwaysVisibleColumns = ['candidate', 'actions'];

    return columns
      .filter(
        (col) =>
          !alwaysVisibleColumns.includes(col.accessor) &&
          columnVisibility[col.accessor] === false,
      )
      .map((col) => col.accessor);
  }, [columns, columnVisibility]);

  useEffect(() => {
    if (toggleHideColumn) {
      columns.forEach((column) => {
        if (column.accessor) {
          toggleHideColumn(column.accessor, !columnVisibility[column.accessor]);
        }
      });
    }
  }, [columnVisibility, toggleHideColumn, columns]);

  return {
    columnVisibility,
    toggleColumnVisibility,
    getHiddenColumns,
  };
};

export default useColumnVisibility;
